import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { Addresses as AddressesCmp } from '../../components/Addresses';
import { ThirdPartyAddressesFormStep } from '../../forms/ThirdParty/steps/Addresses';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { AddressPaths } from '../../forms/ThirdParty/steps/Addresses/types/paths.type';

export const OrganizationPage_Addresses_Organization_Fragment = graphql(`
  fragment OrganizationPage_Addresses_Organization_Fragment on Organization {
    _id
    isInternal
    kind
    statusId
    addresses {
      _id
      typeIds
      postalInfo {
        _id
        postOfficeBox
        postalCode
        region
        route
        streetNumber
        additional
        building
        businessPostCode {
          postalCode
          distributorOfficeNumber
        }
        locality
        inseeCode
        department
        countryAlpha2
      }
      locationInfo {
        _id
        coordinates
        projectionId
        altitude
      }
      cadastralInfo {
        _id
        departmentCode
        districtCode
        municipalityCode
        number
        oldMunicipalityCode
        section
        sheet
      }
      ...AddressesCmp_Address_Fragment
    }
    pennylaneCustomerBridges {
      _id
    }
    ...ThirdPartyForm_Organization_Fragment
    ...ThirdPartyForm_AddressesStep_ThirdParty_Fragment
  }
`);

interface Props {
  readonly paths?: AddressPaths;
  readonly organization: FragmentType<
    typeof OrganizationPage_Addresses_Organization_Fragment
  >;
}

export const Addresses = ({
  organization: pOrganization,
  paths,
}: Readonly<Props>) => {
  const { addressableConfigsCodeMap } = useReferencesContext();

  const organization = getFragmentData(
    OrganizationPage_Addresses_Organization_Fragment,
    pOrganization,
  );

  const { pathname: currentPath } = useLocation();

  const isInEditionMode = paths && currentPath !== paths.consult;

  const addressableConfig = organization.isInternal
    ? addressableConfigsCodeMap.InternalOrganization
    : organization.pennylaneCustomerBridges?.length
      ? addressableConfigsCodeMap.ExternalThirdPartySyncWithPennylane
      : addressableConfigsCodeMap.ExternalThirdParty;

  return (
    <>
      {!isInEditionMode ? (
        <AddressesCmp
          actionsEnabled={false}
          addresses={organization.addresses}
        />
      ) : (
        <Formik enableReinitialize initialValues={organization} onSubmit={noop}>
          <ThirdPartyAddressesFormStep
            config={addressableConfig}
            id="organizationAddresses"
            paths={paths}
            useDirectMutation
          />
        </Formik>
      )}
      {!!paths && (
        <SpeedDial
          actions={[
            {
              id: isInEditionMode
                ? 'leaveEditionMode'
                : 'editOrganizationAddresses',
              icon: isInEditionMode ? <EditOffIcon /> : <EditIcon />,
              name: isInEditionMode
                ? 'Quitter Mode Édition'
                : 'Modifier Adresses',
              to: isInEditionMode ? paths.consult : paths.edit,
            },
          ]}
          title="Actions possibles sur les Adresses de l'Organisation"
        />
      )}
    </>
  );
};
